<template>
  <v-dialog :value="value" max-width="1080" scrollable @input="$emit('input', $event)" @keydown.esc="close">
    <v-card class="detail-card">
      <v-card-title class="detail-header">
        <div class="title-group">
          <span class="text-h6 font-weight-bold">{{ title }}</span>
          <settlement-status-badge v-if="detail" :status="detail.status"></settlement-status-badge>
          <span v-if="detail" class="header-subtitle">{{ detail.clinicName }} · Corte {{ formatDate(detail.cutoffDate) }}</span>
        </div>
        <v-btn icon small aria-label="Cerrar" @click="close"><v-icon>mdi-close</v-icon></v-btn>
      </v-card-title>
      <v-divider></v-divider>

      <v-card-text class="pa-0 detail-body">
        <div v-if="loading" class="pa-8 text-center">
          <v-progress-circular indeterminate color="primary"></v-progress-circular>
        </div>
        <v-alert v-else-if="loadError" type="error" outlined dense class="ma-6">{{ loadError }}</v-alert>

        <div v-else-if="detail" class="detail-layout">
          <!-- Columna principal: datos, documentos y atenciones, siempre visibles -->
          <div class="main-column">
            <div v-if="note" class="note" :style="noteStyle">
              <v-icon small :color="noteStyle.color">mdi-information-outline</v-icon>
              <span>{{ note }}</span>
            </div>

            <v-alert v-for="warning in reviewWarnings" :key="warning" type="warning" outlined dense class="mb-0">
              {{ warning }}
            </v-alert>

            <div class="data-grid">
              <div v-for="field in dataFields" :key="field.label">
                <div class="pw-label">{{ field.label }}</div>
                <div class="pw-value" :style="field.style">{{ field.value }}</div>
              </div>
            </div>

            <div v-if="documentLinks.length || detail.status === 'paid'" class="documents">
              <a v-for="doc in documentLinks" :key="doc.kind" class="doc-chip" @click="openDocument(doc.kind)">
                <v-icon small>{{ doc.icon }}</v-icon>{{ doc.label }}
              </a>
              <span v-if="detail.status === 'paid' && !hasReceipt" class="doc-missing">Sin comprobante</span>
            </div>

            <v-file-input
              v-if="detail.status === 'paid'"
              v-model="receiptFile"
              :label="hasReceipt ? 'Reemplazar comprobante (PDF o imagen)' : 'Subir comprobante (PDF o imagen)'"
              accept=".pdf,image/*"
              prepend-icon=""
              prepend-inner-icon="mdi-paperclip"
              :loading="uploadingReceipt"
              :disabled="uploadingReceipt"
              outlined
              dense
              hide-details="auto"
              @change="uploadReceipt"
            ></v-file-input>

            <section>
              <div class="block-header">
                <h3 class="block-title">Atenciones del corte</h3>
                <span class="block-meta">{{ attendancesLabel(detail.attendances) }} · {{ formatClp(detail.pawerAmount) }}</span>
              </div>
              <div class="attendances-box" tabindex="0" role="region" aria-label="Atenciones del corte">
                <settlement-attendance-list
                  :items="detail.attendanceItems"
                  :editable="isEditable"
                  @cancel="askCancel"
                  @restore="askRestore"
                ></settlement-attendance-list>
              </div>
            </section>

            <section v-if="showInvoiceForm" class="invoice-box">
              <button
                type="button"
                class="invoice-toggle"
                :aria-expanded="String(invoiceFormOpen)"
                @click="invoiceFormOpen = !invoiceFormOpen"
              >
                <span>{{ detail.invoiceNumber ? 'Corregir folio, fecha o documentos' : 'Registrar la factura de la clínica' }}</span>
                <v-icon small>{{ invoiceFormOpen ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
              </button>
              <register-settlement-invoice-form
                v-if="invoiceFormOpen"
                :key="invoiceFormKey"
                class="mt-4"
                :clinic-id="clinicId"
                :cutoff-date="cutoffDate"
                :invoice-number="detail.invoiceNumber"
                :invoice-issued-at="detail.invoiceIssuedAt"
                :has-pdf="!!(detail.documents && detail.documents.invoicePdf)"
                :has-xml="!!(detail.documents && detail.documents.invoiceXml)"
                @saved="onUpdated($event, 'Factura guardada.')"
              ></register-settlement-invoice-form>
            </section>
          </div>

          <!-- Panel lateral: estado e historial, siempre visibles -->
          <aside class="side-panel">
            <section class="side-block">
              <h3 class="side-title">ESTADO</h3>
              <div role="radiogroup" aria-label="Estado de la liquidación" class="status-options">
                <button
                  v-for="key in statusKeys"
                  :key="key"
                  type="button"
                  role="radio"
                  :aria-checked="String(statusChoice === key)"
                  class="status-option"
                  :class="{ 'status-option--selected': statusChoice === key }"
                  @click="statusChoice = key"
                >
                  <span class="status-dot" :style="{ background: statusMeta(key).dot }"></span>
                  <span class="status-name">{{ statusMeta(key).label }}</span>
                  <span v-if="detail.status === key" class="status-current">actual</span>
                </button>
              </div>
              <p class="status-description">{{ statusDescription }}</p>
              <v-textarea
                v-if="statusChoice === 'canceled' && isStatusChanged"
                v-model="cancellationReason"
                label="Motivo de la anulación *"
                counter="255"
                maxlength="255"
                rows="2"
                auto-grow
                outlined
                dense
                background-color="white"
              ></v-textarea>
              <template v-if="asksManualCreditNote">
                <v-textarea
                  v-model="creditNoteReason"
                  label="Motivo de la Nota de Crédito *"
                  placeholder="Ej: factura por un monto mayor"
                  counter="255"
                  maxlength="255"
                  rows="2"
                  auto-grow
                  outlined
                  dense
                  background-color="white"
                ></v-textarea>
                <v-text-field
                  v-model="creditNoteAmountInput"
                  label="Monto de la NC (opcional)"
                  prefix="$"
                  inputmode="numeric"
                  :hint="creditNoteAmountHint"
                  persistent-hint
                  outlined
                  dense
                  background-color="white"
                  class="mb-3"
                ></v-text-field>
              </template>
              <v-alert v-if="statusError" type="error" dense outlined class="mb-0">{{ statusError }}</v-alert>
              <v-btn color="primary" depressed block :loading="savingStatus" :disabled="!canSaveStatus" @click="saveStatus">
                Guardar cambio de estado
              </v-btn>
            </section>

            <section class="side-block">
              <h3 class="side-title">HISTORIAL</h3>
              <settlement-history-timeline :events="detail.events"></settlement-history-timeline>
            </section>
          </aside>
        </div>
      </v-card-text>
    </v-card>

    <cancel-attendance-dialog
      v-model="showCancelDialog"
      :clinic-id="clinicId"
      :cutoff-date="cutoffDate"
      :attendance="cancelTarget"
      :settlement="detail"
      @canceled="onUpdated($event, 'Atención anulada.')"
    ></cancel-attendance-dialog>

    <restore-attendance-dialog
      v-model="showRestoreDialog"
      :clinic-id="clinicId"
      :cutoff-date="cutoffDate"
      :attendance="restoreTarget"
      :settlement="detail"
      @restored="onUpdated($event, 'Atención restaurada.')"
    ></restore-attendance-dialog>

    <pdf-viewer-dialog v-model="viewer.open" :src="viewer.src" :title="viewer.title"></pdf-viewer-dialog>

    <v-snackbar v-model="showSnackbar" :color="snackbarColor" :timeout="4000">{{ snackbarMessage }}</v-snackbar>
  </v-dialog>
</template>

<script>
import ClinicSettlementsService from '@/services/clinic-settlements.service';
import SettlementStatusBadge from './SettlementStatusBadge.vue';
import SettlementAttendanceList from './SettlementAttendanceList.vue';
import SettlementHistoryTimeline from './SettlementHistoryTimeline.vue';
import RegisterSettlementInvoiceForm from './RegisterSettlementInvoiceForm.vue';
import CancelAttendanceDialog from './CancelAttendanceDialog.vue';
import RestoreAttendanceDialog from './RestoreAttendanceDialog.vue';
import PdfViewerDialog from '@/components/PdfViewerDialog.vue';
import { utilsMixin } from '@/mixins/utilsMixin';
import RutUtils from '@/utils/rut.utils';
import {
  STATUS_META,
  STATUS_KEYS,
  STATUS_DESCRIPTIONS,
  isManualCreditNote,
  parseClpInput,
  suggestedCreditNoteAmount,
  EDITABLE_STATUSES,
  attendancesLabel,
  formatClp,
  formatDate,
  pdfReviewWarnings,
  settlementErrorMessage,
  statusNote
} from '@/utils/clinic-settlements.utils';

const MAX_RECEIPT_BYTES = 10 * 1024 * 1024;
const MIN_REASON_LENGTH = 3;

// Title of the in-app viewer per document kind.
const DOCUMENT_TITLES = {
  'invoice-pdf': 'Factura',
  'credit-note-pdf': 'Documento de ajuste',
  receipt: 'Comprobante de transferencia'
};

// Documents the settlement may have, in display order.
const DOCUMENT_LINKS = [
  { kind: 'invoice-pdf', flag: 'invoicePdf', label: 'Ver factura (PDF)', icon: 'mdi-file-pdf-box' },
  { kind: 'invoice-xml', flag: 'invoiceXml', label: 'Descargar XML', icon: 'mdi-file-code-outline' },
  { kind: 'credit-note-pdf', flag: 'creditNotePdf', label: 'Ver documento de ajuste', icon: 'mdi-file-document-outline' },
  { kind: 'receipt', flag: 'paymentReceipt', label: 'Ver comprobante', icon: 'mdi-receipt-text-outline' }
];

// Detalle de una liquidación (clínica × corte), en dos columnas: a la izquierda los datos,
// documentos y atenciones; a la derecha el estado y el historial, siempre visibles.
// Tras cada cambio reemplaza el detalle por el que devuelve el backend y emite `changed`
// para que la vista recargue la tabla.
export default {
  name: 'ClinicSettlementDetailDialog',
  components: {
    SettlementStatusBadge,
    SettlementAttendanceList,
    SettlementHistoryTimeline,
    RegisterSettlementInvoiceForm,
    CancelAttendanceDialog,
    RestoreAttendanceDialog,
    PdfViewerDialog
  },
  mixins: [utilsMixin],
  props: {
    value: { type: Boolean, default: false },
    clinicId: { type: String, default: '' },
    cutoffDate: { type: String, default: '' }
  },
  data() {
    return {
      detail: null,
      loading: false,
      loadError: '',
      invoiceFormOpen: false,
      statusKeys: STATUS_KEYS,
      statusChoice: null,
      cancellationReason: '',
      creditNoteReason: '',
      creditNoteAmountInput: '',
      savingStatus: false,
      statusError: '',
      showCancelDialog: false,
      cancelTarget: null,
      showRestoreDialog: false,
      restoreTarget: null,
      receiptFile: null,
      uploadingReceipt: false,
      viewer: { open: false, src: '', title: '' },
      showSnackbar: false,
      snackbarMessage: '',
      snackbarColor: ''
    };
  },
  computed: {
    title() {
      if (!this.detail) return 'Detalle de factura';
      return this.detail.invoiceNumber ? `Factura N° ${this.detail.invoiceNumber}` : 'Pre-liquidación';
    },
    note() {
      return statusNote(this.detail);
    },
    reviewWarnings() {
      return pdfReviewWarnings(this.detail);
    },
    noteStyle() {
      const meta = STATUS_META[this.detail.status] || STATUS_META.not_invoiced;
      return { background: meta.bg, color: meta.fg };
    },
    dataFields() {
      const d = this.detail;
      const amountColor = d.status === 'credit_note_required' ? { color: STATUS_META.credit_note_required.strong } : {};
      return [
        { label: 'Clínica', value: d.clinicName },
        { label: 'RUT', value: RutUtils.formatIdentifier(d.clinicRut) },
        { label: 'Fecha de corte', value: formatDate(d.cutoffDate) },
        { label: 'Fecha de emisión', value: formatDate(d.invoiceIssuedAt) || 'Pendiente' },
        { label: 'Atenciones', value: attendancesLabel(d.attendances) },
        { label: 'Monto Pawer', value: formatClp(d.pawerAmount), style: amountColor }
      ];
    },
    documentLinks() {
      const documents = (this.detail && this.detail.documents) || {};
      return DOCUMENT_LINKS.filter((doc) => documents[doc.flag]);
    },
    hasReceipt() {
      return !!(this.detail && this.detail.documents && this.detail.documents.paymentReceipt);
    },
    isEditable() {
      return !!this.detail && EDITABLE_STATUSES.includes(this.detail.status);
    },
    showInvoiceForm() {
      return this.isEditable && this.detail.isCutoffClosed === true;
    },
    invoiceFormKey() {
      const d = this.detail;
      return [d.invoiceNumber, d.invoiceIssuedAt, JSON.stringify(d.documents || {})].join('|');
    },
    isStatusChanged() {
      return !!this.detail && !!this.statusChoice && this.statusChoice !== this.detail.status;
    },
    statusDescription() {
      if (this.statusChoice === 'credit_note_required' && isManualCreditNote(this.detail)) {
        return 'Se le pide a la clínica una Nota de Crédito para corregir su factura (por ejemplo, si se emitió por un monto distinto). No requiere anular atenciones y el Monto Pawer no cambia.';
      }
      return STATUS_DESCRIPTIONS[this.statusChoice] || '';
    },
    asksManualCreditNote() {
      return this.isStatusChanged && this.statusChoice === 'credit_note_required' && isManualCreditNote(this.detail);
    },
    creditNoteAmount() {
      return parseClpInput(this.creditNoteAmountInput);
    },
    creditNoteAmountHint() {
      const suggested = suggestedCreditNoteAmount(this.detail);
      return suggested
        ? `Sugerido: ${formatClp(suggested)} (factura − Monto Pawer).`
        : 'Lo que debe sumar la NC, si lo sabes.';
    },
    canSaveStatus() {
      if (!this.isStatusChanged || this.savingStatus) return false;
      if (this.statusChoice === 'canceled') return this.cancellationReason.trim().length >= MIN_REASON_LENGTH;
      if (this.asksManualCreditNote) {
        const amountOk = !this.creditNoteAmountInput.trim() || this.creditNoteAmount !== null;
        return this.creditNoteReason.trim().length >= MIN_REASON_LENGTH && amountOk;
      }
      return true;
    }
  },
  watch: {
    asksManualCreditNote(asks) {
      if (asks && !this.creditNoteAmountInput) {
        const suggested = suggestedCreditNoteAmount(this.detail);
        if (suggested) this.creditNoteAmountInput = suggested.toLocaleString('es-CL');
      }
    },
    value: {
      immediate: true,
      handler(open) {
        if (open) this.load();
      }
    }
  },
  methods: {
    attendancesLabel,
    formatClp,
    formatDate,
    statusMeta(key) {
      return STATUS_META[key];
    },
    statusLabel(key) {
      return STATUS_META[key].label;
    },
    close() {
      this.$emit('input', false);
    },
    resetStatusForm() {
      this.statusChoice = this.detail ? this.detail.status : null;
      this.cancellationReason = '';
      this.creditNoteReason = '';
      this.creditNoteAmountInput = '';
      this.statusError = '';
    },
    async load() {
      if (!this.clinicId || !this.cutoffDate) return;
      this.loading = true;
      this.loadError = '';
      this.detail = null;
      const res = await ClinicSettlementsService.getDetail(this.clinicId, this.cutoffDate);
      this.loading = false;
      if (res.status !== 'ok') {
        this.loadError = settlementErrorMessage(res, 'No se pudo cargar el detalle de la liquidación.');
        return;
      }
      this.detail = res.data;
      // Registering the clinic's invoice is the next step only while it has none.
      this.invoiceFormOpen = !res.data.invoiceNumber;
      this.resetStatusForm();
    },
    onUpdated(detail, message) {
      this.detail = detail;
      this.invoiceFormOpen = false;
      this.resetStatusForm();
      this.$emit('changed');
      this.showSuccessSnackbar(message);
    },
    async saveStatus() {
      if (!this.canSaveStatus) return;
      this.savingStatus = true;
      this.statusError = '';
      const res = await ClinicSettlementsService.changeStatus(this.clinicId, this.cutoffDate, {
        status: this.statusChoice,
        reason: (this.asksManualCreditNote ? this.creditNoteReason : this.cancellationReason).trim(),
        creditNoteAmount: this.asksManualCreditNote ? this.creditNoteAmount : null
      });
      this.savingStatus = false;
      if (res.status !== 'ok') {
        this.statusError = settlementErrorMessage(res, 'No se pudo cambiar el estado. Intenta nuevamente.');
        return;
      }
      this.onUpdated(res.data, 'Estado actualizado.');
    },
    askCancel(item) {
      this.cancelTarget = item;
      this.showCancelDialog = true;
    },
    askRestore(item) {
      this.restoreTarget = item;
      this.showRestoreDialog = true;
    },
    async openDocument(kind) {
      const res = await ClinicSettlementsService.getDocumentUrl(this.clinicId, this.cutoffDate, kind);
      if (res.status !== 'ok') {
        this.showErrorSnackbar(settlementErrorMessage(res, 'No se pudo abrir el archivo.'));
        return;
      }
      // The XML is a download, not something to read on screen.
      if (kind === 'invoice-xml') {
        window.open(res.data.url, '_blank', 'noopener');
        return;
      }
      this.viewer = { open: true, src: res.data.url, title: DOCUMENT_TITLES[kind] };
    },
    async uploadReceipt(file) {
      if (!file) return;
      if (file.size > MAX_RECEIPT_BYTES) {
        this.showErrorSnackbar('El comprobante supera los 10 MB.');
        this.receiptFile = null;
        return;
      }
      this.uploadingReceipt = true;
      const res = await ClinicSettlementsService.uploadReceipt(this.clinicId, this.cutoffDate, file);
      this.uploadingReceipt = false;
      this.receiptFile = null;
      if (res.status !== 'ok') {
        this.showErrorSnackbar(settlementErrorMessage(res, 'No se pudo subir el comprobante.'));
        return;
      }
      this.onUpdated(res.data, 'Comprobante subido.');
    }
  }
};
</script>

<style scoped>
.detail-header {
  display: flex;
  justify-content: space-between;
  flex-wrap: nowrap;
  gap: 12px;
}
.title-group {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
}
.header-subtitle {
  font-size: 14px;
  font-weight: 400;
  color: var(--pw-text-muted);
}
.detail-body {
  max-height: 78vh;
}
.detail-layout {
  display: flex;
  align-items: stretch;
  min-height: 100%;
}
.main-column {
  flex: 1 1 auto;
  min-width: 0;
  padding: 20px 24px;
  display: flex;
  flex-direction: column;
  gap: 18px;
}
.side-panel {
  flex: 0 0 340px;
  padding: 20px;
  border-left: 1px solid var(--pw-border);
  background: #f8fafc;
  display: flex;
  flex-direction: column;
  gap: 24px;
}
/* Panel angosto (340px): los campos del cambio de estado van al tamaño del resto del panel. */
.side-block >>> .v-input textarea,
.side-block >>> .v-input input {
  font-size: 13px;
}
.side-block >>> .v-input textarea::placeholder,
.side-block >>> .v-input input::placeholder {
  font-size: 13px;
  color: var(--pw-text-faint);
}
.side-block >>> .v-messages {
  font-size: 11px;
  hyphens: none;
}
.side-block {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.side-title {
  margin: 0;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.6px;
  color: var(--pw-text-muted);
}
.status-options {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.status-option {
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 44px;
  padding: 0 12px;
  border: 1px solid var(--pw-border);
  border-radius: 10px;
  background: #ffffff;
  font-size: 14px;
  text-align: left;
  cursor: pointer;
}
.status-option--selected {
  border: 2px solid var(--v-primary-base, #1e63d6);
  padding: 0 11px;
  font-weight: 600;
}
.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 999px;
  flex-shrink: 0;
}
.status-name {
  flex: 1 1 auto;
}
.status-current {
  font-size: 12px;
  font-weight: 400;
  color: var(--pw-text-muted);
}
.status-description {
  margin: 0;
  font-size: 13px;
  line-height: 1.45;
  color: var(--pw-text-muted);
}
.note {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 14px;
  border-radius: 10px;
  font-size: 13px;
  line-height: 1.45;
}
.data-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px 16px;
}
.documents {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}
.doc-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 36px;
  padding: 0 12px;
  border: 1px solid var(--pw-border);
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  text-decoration: none;
}
.doc-missing {
  font-size: 13px;
  color: var(--pw-text-faint);
}
.block-header {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 12px;
  margin-bottom: 8px;
}
.block-title {
  margin: 0;
  font-size: 16px;
  font-weight: 700;
}
.block-meta {
  font-size: 13px;
  color: var(--pw-text-muted);
}
/* A cut can carry dozens of attendances: the list scrolls on its own so the
   invoice form below stays reachable and the side panel stays in view. */
.attendances-box {
  border: 1px solid var(--pw-border);
  border-radius: 12px;
  padding: 0 16px;
  max-height: 360px;
  overflow-y: auto;
  overscroll-behavior: contain;
}
.invoice-box {
  border: 1px solid var(--pw-border);
  border-radius: 12px;
  padding: 14px 16px;
}
.invoice-toggle {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  background: none;
  border: 0;
  padding: 0;
  font-size: 14px;
  font-weight: 600;
  color: var(--v-primary-base, #1e63d6);
  cursor: pointer;
}
@media (max-width: 960px) {
  .detail-layout {
    flex-direction: column;
  }
  .side-panel {
    flex-basis: auto;
    border-left: 0;
    border-top: 1px solid var(--pw-border);
  }
}
@media (max-width: 600px) {
  .data-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
</style>
